/**
 * ECONOMÍA de Bellotas — lógica PURA (sin DB, sin framework), unit-testada.
 *
 * El SERVIDOR es la única fuente de verdad del saldo (server-authoritative). Estas
 * funciones NO deciden nada autoritativo: el cliente las usa para (a) proyectar el
 * saldo desde el ledger sincronizado y (b) pintar affordability / topes / racha
 * sin round-trip. Cualquier gasto lo revalida el servidor (anti-fraude).
 *
 * Todo en UNIDADES ENTERAS de bellotas: sin decimales, sin floats.
 */

import { VIDEO_DAILY_CAP } from "./constants";

/** Línea mínima del ledger para calcular el saldo. */
export interface LedgerLike {
  delta: number | null;
}

/**
 * Saldo = suma de los deltas del ledger. Robusto ante datos sucios (null / no
 * finitos se ignoran). Es la MISMA verdad que `wallet.balance_acorns`: el ledger
 * y el saldo materializado deben coincidir (lo asegura el servidor por tx).
 */
export function computeBalanceFromLedger(ledger: readonly LedgerLike[]): number {
  let sum = 0;
  for (const row of ledger) {
    const d = row.delta;
    if (typeof d === "number" && Number.isFinite(d)) {
      sum += Math.trunc(d);
    }
  }
  return sum;
}

/**
 * ¿Puede el usuario permitirse un canje? El saldo negativo se satura a 0 y el
 * coste negativo/no finito se trata como gratuito (0). NO autoriza nada: el
 * servidor revalida el saldo dentro de la transacción del canje.
 */
export function canAffordRedemption(balance: number, cost: number): boolean {
  const safeBalance = Number.isFinite(balance) && balance > 0 ? Math.trunc(balance) : 0;
  const safeCost = Number.isFinite(cost) && cost > 0 ? Math.trunc(cost) : 0;
  return safeBalance >= safeCost;
}

/**
 * Vídeos recompensados que quedan hoy (para el CTA "Ganar bellotas"). Acota a
 * [0, cap]. El cumplimiento real del tope lo hace el servidor; esto sólo apaga el
 * botón cuando ya no quedan.
 */
export function rewardVideosRemaining(seenToday: number, cap: number = VIDEO_DAILY_CAP): number {
  const safeCap = Number.isFinite(cap) && cap > 0 ? Math.trunc(cap) : 0;
  const safeSeen = Number.isFinite(seenToday) && seenToday > 0 ? Math.trunc(seenToday) : 0;
  return Math.max(0, safeCap - Math.min(safeSeen, safeCap));
}

/** ¿Queda algún vídeo recompensado hoy? */
export function canWatchRewardVideo(seenToday: number, cap: number = VIDEO_DAILY_CAP): boolean {
  return rewardVideosRemaining(seenToday, cap) > 0;
}

/**
 * Día de racha que correspondería a la PRÓXIMA reclamación diaria. Si ayer se
 * reclamó, continúa la racha (prev+1); si no, se reinicia a 1. Es informativo
 * (el importe base es fijo); el servidor persiste el valor autoritativo.
 */
export function nextStreakDay(claimedYesterday: boolean, prevStreak: number): number {
  if (!claimedYesterday) {
    return 1;
  }
  const safePrev = Number.isFinite(prevStreak) && prevStreak > 0 ? Math.trunc(prevStreak) : 0;
  return safePrev + 1;
}
